/**
 * 視聴フロントエンド設定の解決モジュール
 *
 * チャット・テスト問題（AppSync Events）の接続情報と外部ドメインを、
 * エンドユーザーAPIの frontend-settings（テナントDBの appsync_settings /
 * external_domain_settings、管理画面で登録）から実行時に取得する。
 * 環境変数へのフォールバックは持たない（未設定の場合は各機能が無効になる）。
 *
 * 使い方:
 * - 非同期の入口（API関数・WebSocket接続開始）で ensureFrontendSettings() を await する
 * - 同期でしか呼べない箇所（subscribe送信・可用性判定）は getCachedFrontendSettings() を参照する
 *   （入口で ensure 済みであることが前提）
 */

const END_USER_DOMAIN = import.meta.env.VITE_END_USER_DOMAIN || '';

/** frontend-settings APIのレスポンス */
interface FrontendSettingsApiResponse {
  status: string;
  data: {
    chat: {
      realtime_endpoint: string;
      http_endpoint: string;
      api_key: string;
      history_api_endpoint: string;
    };
    question: {
      realtime_endpoint: string;
      http_endpoint: string;
      api_key: string;
      rest_api_url: string;
    };
    domains: {
      elive_domain: string;
      manabun_domain: string;
      official_domain: string;
    };
  };
}

/** 解決済みの視聴フロントエンド設定 */
export interface FrontendSettings {
  chatRealtimeEndpoint: string;
  chatHttpEndpoint: string;
  chatApiKey: string;
  chatHistoryApiEndpoint: string;
  questionRealtimeEndpoint: string;
  questionHttpEndpoint: string;
  questionApiKey: string;
  questionRestApiUrl: string;
  eliveDomain: string;
  manabunDomain: string;
  officialDomain: string;
}

// ページロード中は同じ設定を使い続ける（リロードで再取得）
let cachedSettings: FrontendSettings | null = null;
let inflight: Promise<FrontendSettings> | null = null;

/**
 * 現在のURLパスからテナントスラッグを取得（/:tenantSlug/lecture/... 形式）
 */
function getTenantSlugFromPath(): string | null {
  const match = window.location.pathname.match(/^\/([^/]+)\//);
  return match?.[1] ?? null;
}

/**
 * 視聴フロントエンド設定を取得する（初回のみAPIを呼び、以降はキャッシュを返す）
 */
export async function ensureFrontendSettings(): Promise<FrontendSettings> {
  if (cachedSettings) {
    return cachedSettings;
  }

  // 同時多発の取得は1リクエストにまとめる
  if (inflight) {
    return inflight;
  }

  const tenantSlug = getTenantSlugFromPath();
  if (!tenantSlug) {
    throw new Error('テナントを特定できないため設定を取得できません。');
  }

  inflight = (async () => {
    const url = `${END_USER_DOMAIN}/api/v1/auth/end_user/${tenantSlug}/frontend-settings`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`設定情報の取得に失敗しました: ${response.status}`);
    }

    const json = (await response.json()) as FrontendSettingsApiResponse;
    const data = json.data;

    cachedSettings = {
      chatRealtimeEndpoint: data?.chat?.realtime_endpoint || '',
      chatHttpEndpoint: data?.chat?.http_endpoint || '',
      chatApiKey: data?.chat?.api_key || '',
      chatHistoryApiEndpoint: data?.chat?.history_api_endpoint || '',
      questionRealtimeEndpoint: data?.question?.realtime_endpoint || '',
      questionHttpEndpoint: data?.question?.http_endpoint || '',
      questionApiKey: data?.question?.api_key || '',
      questionRestApiUrl: data?.question?.rest_api_url || '',
      eliveDomain: data?.domains?.elive_domain || '',
      manabunDomain: data?.domains?.manabun_domain || '',
      officialDomain: data?.domains?.official_domain || '',
    };

    return cachedSettings;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/**
 * キャッシュ済みの設定を取得（未取得の場合は null）
 */
export function getCachedFrontendSettings(): FrontendSettings | null {
  return cachedSettings;
}
